// Import model
import { ProjectTask } from '../models/ProjectTask';

// Import base class serializer
import { ObjectTaskSerializer } from './ObjectTaskSerializer';

// Import dependency types (models and enums used in properties)
import { Project } from '../models/Project';
import { ReferencedObject } from '../models/ReferencedObject';

// Import dependency serializers (for complex types)
import { ProjectSerializer } from './ProjectSerializer';
import { ReferencedObjectSerializer } from './ReferencedObjectSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for ProjectTask - handles JSON serialization and deserialization with type validation
 */
export class ProjectTaskSerializer {
  /**
   * Deserializes a JSON string into a ProjectTask instance
   */
  static deserialize(json: string): ProjectTask {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a ProjectTask instance
   */
  static fromObject(obj: any): ProjectTask {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to ProjectTask');
    }


    const instance: ProjectTask = {
      assignedUserId: obj['assignedUserId'] as string | undefined,
      assignedUserIdName: obj['assignedUserId|Name'] as string | undefined,
      dueDate: obj['dueDate'] as string | undefined,
      priority: obj['priority'] as string | undefined,
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      lastModifiedDate: obj['lastModifiedDate'] as string | undefined,
      content: obj['content'] as string | undefined,
      contentType: obj['contentType'] as string | undefined,
      creatorId: obj['creatorId'] as string | undefined,
      creatorIdName: obj['creatorId|Name'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      relatedObjects: obj['relatedObjects'] as string | undefined,
      parent: obj['parent'] ? ReferencedObjectSerializer.fromObject(obj['parent']) : undefined as any,
      parentObjectType: obj['parentObjectType'] as string,
      parentObjectTypeName: obj['parentObjectType|Name'] as string | undefined,
      parentObjectId: obj['parentObjectId'] as string | undefined,
      _0A1C78A8: obj['_0a1c78a8'] as any | undefined,
      project: obj['project'] ? ProjectSerializer.fromObject(obj['project']) : undefined as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of ProjectTask instances
   */
  static deserializeArray(json: string): ProjectTask[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a ProjectTask instance to a JSON string
   */
  static serialize(instance: ProjectTask): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a ProjectTask instance to a plain object
   */
  static toObject(instance: ProjectTask): any {
    return {
      'assignedUserId': instance.assignedUserId,
      'assignedUserId|Name': instance.assignedUserIdName,
      'dueDate': instance.dueDate,
      'priority': instance.priority,
      'name': instance.name,
      'description': instance.description,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'lastModifiedDate': instance.lastModifiedDate,
      'content': instance.content,
      'contentType': instance.contentType,
      'creatorId': instance.creatorId,
      'creatorId|Name': instance.creatorIdName,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      'isActive': instance.isActive,
      'relatedObjects': instance.relatedObjects,
      'parent': instance.parent ? ReferencedObjectSerializer.toObject(instance.parent) : undefined,
      'parentObjectType': instance.parentObjectType,
      'parentObjectType|Name': instance.parentObjectTypeName,
      'parentObjectId': instance.parentObjectId,
      '_0a1c78a8': instance._0A1C78A8,
      'project': instance.project ? ProjectSerializer.toObject(instance.project) : undefined,
    };
  }

  /**
   * Serializes an array of ProjectTask instances to a JSON string
   */
  static serializeArray(instances: ProjectTask[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
